import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import TurnstileWidget from './TurnstileWidget';

const generateRandomPassword = (length = 16) => {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const digits = '0123456789';
    const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const all = upper + lower + digits + special;
    let password = '';
    password += upper[Math.floor(Math.random() * upper.length)];
    password += lower[Math.floor(Math.random() * lower.length)];
    password += digits[Math.floor(Math.random() * digits.length)];
    password += special[Math.floor(Math.random() * special.length)];
    for (let i = password.length; i < length; i++) {
        password += all[Math.floor(Math.random() * all.length)];
    }
    return password.split('').sort(() => Math.random() - 0.5).join('');
};

const AdminAccounts = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        username: '',
        role: 'admin'
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [admins, setAdmins] = useState([]);
    const [adminsLoading, setAdminsLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);

    const [turnstileTokenCreate, setTurnstileTokenCreate] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [turnstileTokenDelete, setTurnstileTokenDelete] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    const fetchAdmins = useCallback(async () => {
        setAdminsLoading(true);
        try {
            const res = await api.get(API_CONFIG.ADMIN.ACCOUNTS);
            if (res.data.status === 'success') {
                setAdmins(res.data.admins);
            }
        } catch (err) {
            console.error('Failed to fetch admins:', err);
        } finally {
            setAdminsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAdmins();
    }, [fetchAdmins]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
        setSuccess('');
    };

    const handleGeneratePassword = () => {
        const pwd = generateRandomPassword();
        setFormData(prev => ({ ...prev, password: pwd, confirmPassword: pwd }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (!turnstileTokenCreate) {
            setError('Please complete the Turnstile verification');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post(API_CONFIG.ADMIN.CREATE_ACCOUNT, {
                email: formData.email,
                password: formData.password,
                username: formData.username,
                role: formData.role,
                turnstileToken: turnstileTokenCreate
            });

            if (response.data.status === 'success') {
                setSuccess('Admin account created successfully');
                setFormData({ email: '', password: '', confirmPassword: '', username: '', role: 'admin' });
                fetchAdmins();
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to create admin account');
        } finally {
            setLoading(false);
        }
    };

    const handleDisable = async (id) => {
        setActionLoading(`disable-${id}`);
        try {
            const res = await api.put(`/admin/accounts/${id}/disable`);
            if (res.data.status === 'success') {
                fetchAdmins();
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update admin account');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeleteClick = (id) => {
        setDeleteTarget(id);
        setTurnstileTokenDelete(null);
        setDeleteError('');
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        if (!turnstileTokenDelete) {
            setDeleteError('Please complete the Turnstile verification');
            return;
        }

        setActionLoading(`delete-${deleteTarget}`);
        setDeleteError('');
        try {
            const res = await api.delete(`/admin/accounts/${deleteTarget}`, {
                data: { turnstileToken: turnstileTokenDelete }
            });
            if (res.data.status === 'success') {
                setDeleteTarget(null);
                setTurnstileTokenDelete(null);
                fetchAdmins();
            }
        } catch (err) {
            setDeleteError(err.response?.data?.error || 'Failed to delete admin account');
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Admin Accounts</h1>
                <p className="text-gray-600">Create and manage admin accounts</p>
            </div>

            {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {success && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded">
                    {success}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white shadow sm:rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Create New Admin</h2>
                    </div>
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Username</label>
                            <input
                                type="text"
                                name="username"
                                required
                                value={formData.username}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <input
                                type="email"
                                name="email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Role</label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="admin">Admin</option>
                                <option value="super_admin">Super Admin</option>
                            </select>
                        </div>

                        <div>
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-medium text-gray-700">Password</label>
                                <button
                                    type="button"
                                    onClick={handleGeneratePassword}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    Generate Random
                                </button>
                            </div>
                            <div className="mt-1 relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    required
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? (
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                            <div className="mt-1 relative">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    required
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? (
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-center">
                            <TurnstileWidget onVerify={setTurnstileTokenCreate} />
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !turnstileTokenCreate}
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {loading ? 'Creating...' : 'Create Admin Account'}
                        </button>
                    </form>
                </div>

                <div className="bg-white shadow sm:rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Existing Admins</h2>
                    </div>
                    <div className="p-6">
                        {adminsLoading ? (
                            <p className="text-gray-500 text-center py-4">Loading...</p>
                        ) : admins.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">No admin accounts found.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead>
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {admins.map(admin => (
                                            <tr key={admin.id} className="hover:bg-gray-50">
                                                <td className="px-3 py-3 text-sm font-medium text-gray-900">{admin.username}</td>
                                                <td className="px-3 py-3 text-sm text-gray-600">{admin.email}</td>
                                                <td className="px-3 py-3 text-sm">
                                                    <span className={`px-2 py-1 text-xs font-medium rounded ${admin.role === 'super_admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                                        {admin.role}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3 text-sm">
                                                    <span className={`px-2 py-1 text-xs font-medium rounded ${admin.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                        {admin.is_active ? 'Active' : 'Disabled'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3 text-sm text-right space-x-2">
                                                    <button
                                                        onClick={() => handleDisable(admin.id)}
                                                        disabled={actionLoading === `disable-${admin.id}`}
                                                        className={`inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded ${admin.is_active ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-green-100 text-green-800 hover:bg-green-200'} disabled:opacity-50`}
                                                    >
                                                        {admin.is_active ? 'Disable' : 'Enable'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(admin.id)}
                                                        disabled={actionLoading === `delete-${admin.id}`}
                                                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-50"
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => setDeleteTarget(null)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Deletion</h3>
                        <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete this admin account? This action cannot be undone.</p>

                        {deleteError && (
                            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded text-sm">{deleteError}</div>
                        )}

                        <div className="flex justify-center mb-4">
                            <TurnstileWidget onVerify={setTurnstileTokenDelete} />
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteTarget(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={actionLoading === `delete-${deleteTarget}` || !turnstileTokenDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                                {actionLoading === `delete-${deleteTarget}` ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAccounts;